import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { authenticator } from "../Authentication/auth-funcs";
import {
  getAuthHeaders,
  FORM_URLENCODED_CONTENT_TYPE,
} from "../backendIntegration/authHeaders";

const emptyUserProfile = {
  userId: null,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  educationHistory: [],
  jobHistory: [],
  website: "",
  linkedin: "",
  github: "",
  skills: [],
  countryOfResidence: "",
  resume: "",
  resumeText: "",
  experienceLevel: [],
  portfolio: "",
  zipcode: "",
  homeCity: "",
  homeState: "",
  desiredSalary: 0,
  additionalInformation: "",
  gender: "",
  race: "",
  isLgbtq: "",
  veteranStatus: "",
  disabilityStatus: "",
  // US work status replaces the authorized / citizen / sponsorship answers: one of
  // us_citizen | permanent_resident | needs_sponsorship | authorized_no_sponsorship, or null.
  workAuthStatus: null,
  visaType: null,
  visaExpiry: null,
  authorizedToWorkInUK: "",
  authorizedToWorkInCA: "",
  // Miles the user will commute; null until they pick (the server then reads it as 30).
  commuteMiles: null,
  willingToRelocate: "",
  birthday: "",
  pronouns: "",
  willingToCommute: "",
  preferredJobLocations: [],
  acceptedJobs: [],
  rejectedJobs: [],
  appliedJobs: {
    applied: [],
    applying: [],
    screen: [],
    interview: [],
    offer: [],
    failed: [],
  },
  filters: {
    jobIndustry: null,
    location: {
      city: null,
      state: null,
      country: null,
    },
    jobType: [],
    companySize: [],
    postedAfterTimestamp: null,
    postedWithinHours: null,
    minimumSalary: null,
  },
  emailProxyOption: "proxy_domain",
  emailProxyDomain: "",
  emailProxyBaseAddress: "",
  emailProxyStatus: "not_configured",
  joinedSMSList: "notSubscribed",
  joinedEmailList: "notSubscribed",
  availableStartDate: "",
  bestStories: [],
  swipesLeftToday: 5,
  membershipPlan: "free",
  referralKickbackPercentage: 15,
  totalSpent: 0,
  totalPaidOut: 0,
};

// Throws on failure. It must NOT swallow the error into `rejectWithValue` itself:
// RTK only turns that marker into a rejected action when it is the value the
// payload creator returns at the top level, and `fetchUserProfile` nests its
// result under `data`. Doing that made every failed fetch dispatch *fulfilled*
// with `{_type, payload, meta}` as the profile — so `error` never got set, the
// stored profile was replaced by RTK's marker object, and the quiz would then
// POST that marker back over the real profile on Finish.
const fetchProfileCall = async () => {
  const user = await authenticator.getCurrentUser();
  const headers = await getAuthHeaders({
    contentType: FORM_URLENCODED_CONTENT_TYPE,
    includeIdentityId: false,
  });

  const response = await axios.get(
    `${process.env.REACT_APP_API_DOMAIN}/users/${user.userId}/profile`,
    { headers }
  );

  return {
    profile: { userId: user.userId, ...response.data.data },
    // Staleness (NEXT_STAGE_INTAKE) rides BESIDE `data`, not inside it, and is kept out of the
    // profile object here for the same reason the server keeps it out: the profile is what we POST
    // back on save, and a computed observation about the profile has no business making that trip.
    // `null` is the flag being off; `[]` is "we checked and nothing is stale".
    staleness: Array.isArray(response.data.staleness) ? response.data.staleness : null,
  };
};

const profileFetchError = (error) =>
  error?.response ? error.response.data : "An unknown error in profile occurred";

export const fetchUserProfile = createAsyncThunk(
  "user/fetchProfile",
  async (options = {}, { rejectWithValue }) => {
    // options can contain { merge: true } to indicate we should merge with existing data
    try {
      // Pass the merge option through to the reducer via meta
      const { profile, staleness } = await fetchProfileCall();
      return { data: profile, staleness, options };
    } catch (error) {
      return rejectWithValue(profileFetchError(error));
    }
  }
);

export const silentFetchUserProfile = createAsyncThunk(
  "user/silentFetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchProfileCall();
    } catch (error) {
      return rejectWithValue(profileFetchError(error));
    }
  }
);

// Background refresh for pollers (post-checkout entitlement wait). Unlike the
// other two it can NEVER reject: `ProtectedRoute` treats a non-null
// `profileData.error` as an incomplete profile and bounces the user to
// /preferences-quiz, so one flaky poll would throw them off the page they're
// waiting on. It also leaves `loaded` alone so Dashboard still runs its own
// fetch on mount. Resolves to the profile, or null if the fetch failed.
export const pollUserProfile = createAsyncThunk(
  "user/pollProfile",
  async () => {
    try {
      // Only the profile: this poller's callers unwrap the payload and read plan fields off it, and
      // a checkout wait has no use for staleness. Keeping its shape is what stops one flag's wiring
      // from reaching into the payment path.
      return (await fetchProfileCall()).profile;
    } catch {
      return null;
    }
  }
);

// The deck's /handleSwipe thunk (redux/applyCards). Matched by type, not imported:
// applyCards already imports this file.
const SWIPE_ACTION = "jobs/handleSwipe";
const SWIPE_COUNTERS = ["swipesLeftToday", "enduringSwipes"];

/**
 * Swipes the user has left: today's allowance plus banked (enduring) ones, as iOS's
 * chip shows. Null until the profile has loaded, and after a failed load, so the empty
 * profile's default never shows as a count.
 */
export const selectSwipesLeft = (state) => {
  const { loaded, error, data, swipeCounts, swipesInFlight } = state.profileData;
  const counts = swipeCounts || data;
  if (!loaded || (error && !swipeCounts) || typeof counts?.swipesLeftToday !== "number") return null;
  const total = counts.swipesLeftToday + (Number(counts.enduringSwipes) || 0) - (swipesInFlight || 0);
  return Math.max(0, total);
};
/**
 * The deck's out-of-swipes gate: the chip reads 0. That is the profile's counts until a swipe answers, then the
 * server's latest (a 403 at the limit, or a skip's counts after the phone spent the last one), less the applies
 * in flight. Reading the profile's own counts alone missed both: they change only on the next profile read.
 */
export const selectOutOfSwipes = (state) => selectSwipesLeft(state) === 0;
const isNoSwipesLeft = (payload) =>
  [payload?.message, payload?.originalError?.message].some((m) => String(m || "").includes("No swipes left"));

export const userProfileSlice = createSlice({
  name: "userProfile",
  initialState: {
    data: emptyUserProfile,
    loading: false,
    loaded: false,
    error: null,
    // Server-computed staleness findings (NEXT_STAGE_INTAKE). null until a read carries them, and
    // still null afterwards when the flag is off — which is not the same as the empty array the
    // server sends when it checked and found nothing.
    staleness: null,
    // Apply swipes sent to /handleSwipe that have not answered yet, and the counts the
    // last one answered with (null once a newer profile has loaded). See selectSwipesLeft.
    swipesInFlight: 0,
    swipeCounts: null,
  },
  reducers: {
    setLoadingOptimistic: (state) => {
      state.loading = true;
    },
    setLoadingCompleted: (state) => {
      state.loading = false;
    },
    // Replaces the profile wholesale. Only pass a WHOLE profile — `{...data, x}`.
    // A partial payload silently drops every field it omits, which reads
    // downstream as "this account never onboarded" and ejects the user to the
    // preferences quiz. Reach for `updateUserProfileData` unless you really do
    // hold the complete profile.
    setUserProfileData: (state, action) => {
      state.data = action.payload;
      state.loaded = true; // Update the loaded state when manually setting profile data
    },
    // Merges. This is the right one for changing a few fields.
    updateUserProfileData: (state, action) => {
      state.data = { ...state.data, ...action.payload }
      state.loaded = true
    },
    clearUserProfileData: (state) => {
      state.data = emptyUserProfile; // Reset to empty profile
      state.error = null;
      state.loading = false;
      state.loaded = false;
      // Findings are about the profile we just dropped; keeping them would let one user's banner
      // survive into the next session on a shared machine.
      state.staleness = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;

        // Check if we should merge with existing data
        if (action.payload.options && action.payload.options.merge === true) {
          // Merge the new data with existing data, prioritizing existing data for specific fields
          const existingData = state.data;
          const newData = action.payload.data;

          // Fields to preserve from existing data if they exist
          const fieldsToPreserve = [
            'lookingForInCompany',
            'preferredJobLocations',
            'experienceLevel',
            'jobCategoryInterests',
            'skills',
            'desiredSalary'
          ];

          // Create merged data object
          state.data = {
            ...newData,
            // For each field to preserve, use existing data if it's not empty
            ...fieldsToPreserve.reduce((acc, field) => {
              // Only preserve non-empty arrays or non-zero values
              if (Array.isArray(existingData[field]) && existingData[field].length > 0) {
                acc[field] = existingData[field];
              } else if (!Array.isArray(existingData[field]) && existingData[field]) {
                acc[field] = existingData[field];
              }
              return acc;
            }, {})
          };
        } else {
          // Just use the new data directly
          state.data = action.payload.data;
        }
        state.staleness = action.payload.staleness;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload;
      })
      .addCase(silentFetchUserProfile.pending, (state) => {
        state.error = null;
      })
      .addCase(silentFetchUserProfile.fulfilled, (state, action) => {
        state.data = { ...action.payload.profile, filters: state.data.filters };
        state.staleness = action.payload.staleness;
      })
      .addCase(silentFetchUserProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Only `fulfilled`, and only with a payload: a failed poll must leave
      // `data`, `loaded` and `error` exactly as they were.
      .addCase(pollUserProfile.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.data = { ...action.payload, filters: state.data.filters };
      })
      // The swipes-left chip counts down as iOS's does: an apply takes one at once, the
      // counts /handleSwipe answers with replace that, the limit sets both to 0, and any
      // other failure gives the swipe back. Kept apart from `data`: the deck reads
      // data's counts to decide whether a right swipe bounces back, and lowering them
      // while the last allowed swipe is still animating would bounce that swipe too.
      .addMatcher((action) => action.type === `${SWIPE_ACTION}/pending`, (state, action) => {
        if (action.meta.arg?.isAccepting) state.swipesInFlight = (state.swipesInFlight || 0) + 1;
      })
      .addMatcher((action) => action.type === `${SWIPE_ACTION}/fulfilled`, (state, action) => {
        if (action.meta.arg?.isAccepting) state.swipesInFlight = Math.max(0, (state.swipesInFlight || 0) - 1);
        const counts = action.payload?.response;
        if (SWIPE_COUNTERS.every((name) => typeof counts?.[name] === "number")) {
          state.swipeCounts = { swipesLeftToday: counts.swipesLeftToday, enduringSwipes: counts.enduringSwipes };
        }
      })
      .addMatcher((action) => action.type === `${SWIPE_ACTION}/rejected`, (state, action) => {
        if (action.meta.arg?.isAccepting) state.swipesInFlight = Math.max(0, (state.swipesInFlight || 0) - 1);
        if (isNoSwipesLeft(action.payload)) state.swipeCounts = { swipesLeftToday: 0, enduringSwipes: 0 };
      })
      // A fresh profile from the server is newer than the last swipe's counts (it also
      // carries a purchase, a referral reward or the daily reset), so the chip reads it again.
      .addMatcher(
        (action) =>
          [fetchUserProfile.fulfilled, silentFetchUserProfile.fulfilled, pollUserProfile.fulfilled].some(
            (creator) => creator.match(action)
          ) && Boolean(action.payload),
        (state) => {
          state.swipeCounts = null;
        }
      );
  },
});

export const {
  setUserProfileData,
  updateUserProfileData,
  clearUserProfileData,
  setLoadingOptimistic,
  setLoadingCompleted,
} = userProfileSlice.actions;
export default userProfileSlice.reducer;
